import { useRef, useCallback, useEffect } from "react";

const NO_SELECT = "tc-no-select";
let holders = 0;
let releaseTimer = null;

// While a finger is down on a reactable element the whole document is non-selectable;
// otherwise iOS WebKit falls back to selecting the entire page around a user-select:none target.
const lockSelection = () => {
  holders += 1;
  if (releaseTimer) { clearTimeout(releaseTimer); releaseTimer = null; }
  document.documentElement.classList.add(NO_SELECT);
};
const unlockSelection = () => {
  holders = Math.max(0, holders - 1);
  if (holders > 0) return;
  releaseTimer = setTimeout(() => {
    document.documentElement.classList.remove(NO_SELECT);
    try { window.getSelection()?.removeAllRanges(); } catch { /* ignore */ }
  }, 350);
};

// Long-press (touch + mouse) handlers; cancels on scroll/drag. Also suppresses the iOS context menu.
export const useLongPress = (onLongPress, { delay = 450, moveTolerance = 10 } = {}) => {
  const timer = useRef(null);
  const start = useRef(null);
  const fired = useRef(false);
  const locked = useRef(false);

  const release = useCallback(() => {
    if (locked.current) { locked.current = false; unlockSelection(); }
  }, []);

  const clear = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    release();
  }, [release]);

  useEffect(() => release, [release]);

  const begin = useCallback((x, y, target, touch) => {
    fired.current = false;
    start.current = { x, y };
    if (timer.current) clearTimeout(timer.current);
    if (touch && !locked.current) { locked.current = true; lockSelection(); }
    timer.current = setTimeout(() => {
      fired.current = true;
      try { window.getSelection()?.removeAllRanges(); } catch { /* ignore */ }
      if (navigator.vibrate) navigator.vibrate(10);
      onLongPress(target);
    }, delay);
  }, [onLongPress, delay]);

  const move = useCallback((x, y) => {
    if (!start.current) return;
    if (Math.abs(x - start.current.x) > moveTolerance || Math.abs(y - start.current.y) > moveTolerance) clear();
  }, [clear, moveTolerance]);

  return {
    onTouchStart: (e) => begin(e.touches[0].clientX, e.touches[0].clientY, e.currentTarget, true),
    onTouchMove: (e) => move(e.touches[0].clientX, e.touches[0].clientY),
    onTouchEnd: clear,
    onTouchCancel: clear,
    onMouseDown: (e) => { if (e.button === 0) begin(e.clientX, e.clientY, e.currentTarget, false); },
    onMouseMove: (e) => move(e.clientX, e.clientY),
    onMouseUp: clear,
    onMouseLeave: clear,
    onContextMenu: (e) => { e.preventDefault(); if (!fired.current) onLongPress(e.currentTarget); },
    onClickCapture: (e) => { if (fired.current) { e.stopPropagation(); e.preventDefault(); fired.current = false; } },
  };
};
